import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AccountSearchType } from '@app/enums/account-search-type.enum';
import { AccountType } from '@app/enums/account-type.enum';
import { IAccountTransactionHistory, IAccountTransactionHistoryRequestModel } from '@app/interfaces';
import { IAccountBalances } from '@app/interfaces/lsb/account-balances';
import { IAccountForEdit, IAccountForEditCashAccount } from '@app/interfaces/lsb/account-for-edit';
import { IAccountListItem } from '@app/interfaces/lsb/account-list-item';
import { IAccountSearchModel, IAccountSearchOffsetRequestModel } from '@app/interfaces/lsb/account-search';
import { IAccountsListRequestModel } from '@app/interfaces/lsb/accounts-list-request-model';
import { StandardApiResponse } from '@app/interfaces/standard-api-response';
import { AccountActivationParamsModel, AccountPagingModel } from '@app/models/fund-management';
import { AccountPayrollRes } from '@app/modules/fund-management/accounts/models/account-payroll';
import {
	Account,
	AccountBalances,
	AccountTableRow,
	BasicTypeAccount,
	GetAccountsByPageCommand,
	NewAccount,
	ParentAccountList,
} from '@app/modules/fund-management/accounts/models/accounts';
import { MinMaxListModel } from '@app/modules/fund-management/reports/models/budget-analysis.model';
import { AccountMassUpdateModel } from '@app/modules/system-settings/system-setup/account-mass-update/account-mass-update.model';
import { appendParams, isNil } from '@app/utils';
import { AccountElementModel } from '../forms/components/account-range-table/account-element.models';
import { ApiResponse } from '../models/common';

@Injectable()
export class AccountService {
	endpointBase = '/api/account';

	constructor(private readonly http: HttpClient) {}

	// ===========================
	// AccountCodeFirstController
	// ===========================

	/**
	 * Searches for accounts based on the provided search model.
	 * @param model
	 * @returns
	 */
	public searchAccounts(model: IAccountSearchModel): Observable<ParentAccountList> {
		return this.http.get<ParentAccountList>(`${this.endpointBase}/search`, {
			params: {
				accountNumber: model.AccountNumber,
				limit: model.Limit.toString(),
				accountTypeIds: isNil(model.AccountTypeIds) ? [] : model.AccountTypeIds.map((id) => id.toString()),
				filter: model.Filter ? model.Filter.toString() : 'false',
				bankId: model.BankId,
				onlyActive: model.OnlyActive ? model.OnlyActive.toString() : 'false',
				search: isNil(model.Search) ? '' : model.Search.toString(),
				searchMode: isNil(model.SearchMode) ? AccountSearchType.Detailed : model.SearchMode.toString(),
			},
		});
	}

	/**
	 * Searches for account offsets based on the provided offset request model.
	 * If Fund is not designated on the account structure in account setup, results will be empty.
	 * @param model
	 * @returns
	 */
	public searchOffsetsBasedOffFund(model: IAccountSearchOffsetRequestModel): Observable<ParentAccountList> {
		return this.http.get<ParentAccountList>(`${this.endpointBase}/search-offsets-based-off-fund`, {
			params: {
				AccountId: model.AccountId.toString(),
				accountNumber: model.AccountNumber,
				limit: model.Limit.toString(),
				accountTypeIds: isNil(model.AccountTypeIds) ? [] : model.AccountTypeIds.map((id) => id.toString()),
				filter: model.Filter ? model.Filter.toString() : 'false',
				bankId: model.BankId,
				onlyActive: model.OnlyActive ? model.OnlyActive.toString() : 'false',
			},
		});
	}

	public searchAccountElements(
		grantYear: string,
		elementName: string,
		searchText: string,
		accountTypeIds: AccountType[] = [],
		limit = 50,
		usesNcDpiElementMap: boolean = false,
	): Observable<any> {
		return this.http.get<any>(this.endpointBase + '/search-account-elements', {
			params: {
				GrantYear: grantYear ?? '',
				ElementName: elementName,
				SearchText: searchText,
				AccountTypeIds: accountTypeIds.map((id) => id.toString()),
				Limit: limit,
				UsesNcDpiElementMap: usesNcDpiElementMap.toString(),
			},
		});
	}

	/**
	 * Searches for account transactions based on the provided request model.
	 * @param requestModel
	 * @returns
	 */
	public searchAccountTransactions(requestModel: IAccountTransactionHistoryRequestModel): Observable<IAccountTransactionHistory[]> {
		return this.http.get<IAccountTransactionHistory[]>(`${this.endpointBase}/search-account-transactions`, {
			params: {
				AccountId: requestModel.AccountId.toString(),
				FiscalYear: requestModel.FiscalYear?.toString() ?? '',
				From: requestModel.FromDate ? requestModel.FromDate.toISOString() : '',
				To: requestModel.ToDate ? requestModel.ToDate.toISOString() : '',
				Code: requestModel.JournalCode ?? '',
			},
		});
	}

	public getBasicAccount(id: number): Observable<AccountTableRow> {
		return this.http.get<AccountTableRow>(`${this.endpointBase}/get-basic-account/${id}`);
	}

	public verifyAccountsExist(accountNumbers: string[]): Observable<any> {
		const params = appendParams('', accountNumbers);
		return this.http.get(`${this.endpointBase}/verify-accounts-exist`, { params });
	}

	public getElementsMinMax(elementNames: string[]) {
		return this.http
			.get<ApiResponse<'elementsMinMaxList', MinMaxListModel[]>>(this.endpointBase + '/getElementMinMaxList', {
				params: { elementNames },
			})
			.pipe(
				map((response) => {
					const modifiedList = response.elementsMinMaxList.map((element) => ({
						...element,
						disabled: false,
						rowId: null,
					}));
					return { ...response, elementsMinMaxList: modifiedList };
				}),
			);
	}

	public accountMassUpdate(model: AccountMassUpdateModel) {
		return this.http.post<StandardApiResponse<any>>(`${this.endpointBase}/bulk-update`, model);
	}

	public updateAccount(account: IAccountForEdit): Observable<StandardApiResponse<any>> {
		return this.http.put<StandardApiResponse<any>>(`${this.endpointBase}/update`, account);
	}

	public createAccount(account: IAccountForEdit): Observable<StandardApiResponse<any>> {
		return this.http.post<StandardApiResponse<any>>(`${this.endpointBase}/create`, account);
	}

	public deleteAccount(id: number): Observable<StandardApiResponse<any>> {
		const params = new HttpParams().set('accountId', id.toString());
		return this.http.delete<StandardApiResponse<any>>(`${this.endpointBase}/delete`, { params });
	}

	/**
	 * 	 * Retrieves a list of accounts.
	 * 	 * @param request - The request model contains filter information to determine which accounts to retrieve.
	 * 	 * @returns An Observable containing an array of accounts for the specified page.
	 */
	public getAccountsList(request: IAccountsListRequestModel): Observable<IAccountListItem[]> {
		return this.http.post<IAccountListItem[]>(`${this.endpointBase}/accounts-list`, request);
	}

	/**
	 * 	 * Retrieves account details for editing based on the provided account ID.
	 * 	 * @param accountId - The ID of the account to retrieve.
	 * 	 * @returns An Observable containing the account details for editing.
	 */
	public getAccount(accountId: number): Observable<IAccountForEdit> {
		const params = new HttpParams().set('id', accountId.toString());
		return this.http.get<IAccountForEdit>(`${this.endpointBase}/get-account`, { params });
	}

	/**
	 * Retrieves account balances for a specific account and fiscal year.
	 * @param id - The ID of the account.
	 * @param fiscalYear - The fiscal year for which to retrieve balances.
	 * @returns An Observable containing the account balances.
	 */
	public getAccountBalances(id: number, fiscalYear: number): Observable<IAccountBalances> {
		const params = new HttpParams().set('id', id.toString()).set('fiscalYear', fiscalYear.toString());
		return this.http.get<IAccountBalances>(`${this.endpointBase}/account-balances`, { params });
	}

	/**
	 * Retrieves a list of all cash accounts, based on the user's access rights.
	 * @returns An Observable containing an array of cash accounts.
	 */
	public getCashAccountsList(): Observable<IAccountForEditCashAccount[]> {
		return this.http.get<IAccountForEditCashAccount[]>(`${this.endpointBase}/cash-accounts-list`);
	}

	// AccountCodeFirstController END

	// ===========================
	// AccountController
	// ===========================

	// Deprecated: no longer used
	public searchOffsetsForAccount(
		accountId: number,
		accountNumber: string,
		accountTypeId = 0,
		limit = 12,
	): Observable<{ accounts: BasicTypeAccount[] }> {
		return this.http.get<{ accounts: BasicTypeAccount[] }>(this.endpointBase + '/searchOffsetsForAccount', {
			params: {
				accountId,
				accountTypeId,
				accountNumber,
				limit,
			},
		});
	}

	// Deprecated: no longer used
	public getAccountLegacy(accountNumber: number): Observable<Account> {
		return this.http.get<Account>(`${this.endpointBase}/${accountNumber}`);
	}

	// Deprecated: Use getAccountsList instead
	public getAccountList(): Observable<any> {
		return this.http.get(`${this.endpointBase}/list`);
	}

	public verifyAccount(accountNumber: string): Observable<any> {
		return this.http.get(`${this.endpointBase}/verifyAccount`, { params: { accountNumber } });
	}

	getAccountsByPage(accountPaging: GetAccountsByPageCommand): Observable<AccountTableRow[]> {
		return this.http.post<AccountTableRow[]>(this.endpointBase + '/getByPage', accountPaging);
	}

	getNumberOfAccounts(accountPaging: GetAccountsByPageCommand): Observable<number> {
		return this.http.post<number>(this.endpointBase + '/numberOf', accountPaging);
	}

	// Deprecated: Use createAccount instead
	saveAccount(account: AccountTableRow): Observable<{ isValid: boolean }> {
		return this.http.post<{ isValid: boolean }>(this.endpointBase, account);
	}

	getEmployeeCheckJob(accountId, model): Observable<AccountPayrollRes> {
		return this.http.get<AccountPayrollRes>(this.endpointBase + '/getEmployeeCheckJob', {
			params: { accountId, FromDate: model.FromDate, ToDate: model.ToDate, FiscalYear: model.FiscalYear },
		});
	}

	getAccountBalancesForFiscalYear(id, fiscalYear) {
		return this.http.get<AccountBalances>(this.endpointBase + '/accountBalances', {
			params: { id, fiscalYear },
		});
	}

	addAccount(account) {
		return this.http.put<NewAccount>(this.endpointBase, account);
	}

	getAllAccountFunds(): Observable<any> {
		return this.http.get(this.endpointBase + '/getAllAccountFunds');
	}

	getAllAccountObjectCodes(): Observable<any> {
		return this.http.get(this.endpointBase + '/getAllAccountObjectCodes');
	}

	getAllAccountBudgets(accountPagingModel: AccountPagingModel, fiscalYear: number, useHangfire: boolean): Observable<any> {
		const params = new HttpParams().set('fiscalYear', fiscalYear.toString()).set('useHangfire', useHangfire.toString());
		return this.http.post(this.endpointBase + '/get-all-account-budgets', accountPagingModel, { params });
	}

	getActivationAccounts(status: string, accountTemplate: string): Observable<any> {
		return this.http.get(this.endpointBase + '/get-activation-accounts', {
			params: { status, accountTemplate },
		});
	}

	public queueAccountsActivationChangeProcess(model: AccountActivationParamsModel): Observable<any> {
		return this.http.post(`${this.endpointBase}/queue-account-activation-change-process`, model);
	}

	public applyReportElementRangeModelCol(ElementRangeModelCol: AccountElementModel[]): AccountElementModel[] {
		const reportParameters = [];
		ElementRangeModelCol.forEach((acctF) => {
			if (!isNil(acctF.fromValue)) {
				reportParameters.push(acctF);
			}
		});
		return reportParameters;
	}

	public applyReportaccountFiltersType(accountFiltersType) {
		const reportParameters = [];
		let typeObj = { Mask: '', ElementMap: '' };

		accountFiltersType.forEach((acctF) => {
			typeObj = acctF.type;

			acctF.queries.forEach((query) => {
				if (query.AccountTypeId !== -1 && query.ElementIndex && query.QueryOption && query.Value) {
					const paramModel = {
						element: '',
						maxLength: 0,
						maxValue: 0,
						fromValue: 0,
						toValue: 0,
						accountTypeId: 0,
						startIndex: 0,
						queryOption: '',
					};

					const lengths = typeObj.Mask.split('.');
					const objMask = lengths[parseInt(query.ElementIndex, 10)];

					paramModel.accountTypeId = parseInt(query.AccountTypeId, 10);
					paramModel.queryOption = query.QueryOption;
					paramModel.fromValue = parseInt(query.Value, 10);
					paramModel.maxLength = objMask.length;
					paramModel.startIndex = 0;

					let indexCount = 1;
					for (let mIdx = 0; mIdx < parseInt(query.ElementIndex, 10); mIdx++) {
						indexCount += lengths[mIdx].length;
					}

					paramModel.startIndex = indexCount;
					reportParameters.push(paramModel);
				}
			});
		});
		return reportParameters;
	}

	// AccountController END

	// It's possible to change this method to call of API endpoint and using API MaskHelper class in the code points which don't need processing of several masking at once.
	public applyMask(mask: string, accountNumber: string): string {
		if (
			mask === undefined ||
			mask === null ||
			mask.length === 0 ||
			accountNumber === undefined ||
			accountNumber === null ||
			accountNumber.length === 0
		) {
			return '';
		}

		if (mask.length !== accountNumber.length) {
			return '';
		}

		let appliedMask = '';

		for (let charIndex = 0; charIndex < mask.length; charIndex++) {
			const currentMaskChar = mask[charIndex];
			const currentAccountChar = accountNumber[charIndex];

			if ((currentMaskChar === '.' && currentAccountChar !== '.') || (currentMaskChar !== '.' && currentAccountChar === '.')) {
				return '';
			}

			if (currentMaskChar === '?') {
				appliedMask = appliedMask + currentAccountChar;
			} else {
				appliedMask = appliedMask + currentMaskChar;
			}
		}

		return appliedMask;
	}
}
